import { defineStore } from "pinia";
import { reactive, ref } from "vue";

import {
  createMentorCheckout,
  createMentorCheckoutWithMaterials,
  getMentorAvailability,
  getMentorBooking,
  getMentorBookings,
  getMentorMatches,
  requestMentorReschedule,
  requestMentorCancellation,
  type ApplicationSummary,
  type Mentor,
  type MentorAvailability,
  type MentorBookingDashboard,
} from "@/api/mentors";

export type {
  ApplicationSummary,
  Mentor,
  MentorAvailability,
  MentorBooking,
  MentorBookingDashboard,
  MentorSlot,
} from "@/api/mentors";

export interface MentorBookingView {
  id: string;
  status: string;
  mentor: { id: string; alias: string };
  start_at: string;
  duration_minutes: number;
  join_url?: string | null;
  timezone?: string | null;
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback;
}

export const useMentorsStore = defineStore("mentors", () => {
  const matchingApplication = ref<ApplicationSummary | null>(null);
  const matchingMentors = ref<Mentor[]>([]);
  const matchingLoading = ref(true);
  const matchingError = ref("");

  const availability = ref<MentorAvailability | null>(null);
  const availabilityLoading = ref(true);
  const availabilityError = ref("");
  const checkoutLoading = ref(false);

  const booking = ref<MentorBookingView | null>(null);
  const bookingError = ref("");

  const dashboard = ref<MentorBookingDashboard | null>(null);
  const dashboardLoading = ref(true);
  const dashboardError = ref("");
  const pendingRescheduleBookingIds = reactive(new Set<string>());
  const pendingCancellationBookingIds = reactive(new Set<string>());

  let sessionGeneration = 0;
  let matchingGeneration = 0;
  let availabilityGeneration = 0;
  let bookingGeneration = 0;
  let dashboardGeneration = 0;
  let checkoutGeneration = 0;
  let rescheduleObservationGeneration = 0;
  let activeBookingId = "";

  function ownsRequest(requestGeneration: number, currentGeneration: number, session: number): boolean {
    return requestGeneration === currentGeneration && session === sessionGeneration;
  }

  async function loadMatches(applicationId: string): Promise<void> {
    const requestGeneration = ++matchingGeneration;
    const session = sessionGeneration;
    matchingApplication.value = null;
    matchingMentors.value = [];
    matchingError.value = "";
    matchingLoading.value = true;
    try {
      const result = await getMentorMatches(applicationId);
      if (!ownsRequest(requestGeneration, matchingGeneration, session)) return;
      matchingApplication.value = result.application;
      matchingMentors.value = result.mentors;
    } catch (caught) {
      if (!ownsRequest(requestGeneration, matchingGeneration, session)) return;
      matchingError.value = errorMessage(caught, "Could not find mentors.");
    } finally {
      if (ownsRequest(requestGeneration, matchingGeneration, session)) matchingLoading.value = false;
    }
  }

  async function loadAvailability(mentorId: string, applicationId: string): Promise<void> {
    const requestGeneration = ++availabilityGeneration;
    const session = sessionGeneration;
    checkoutGeneration += 1;
    availability.value = null;
    availabilityError.value = "";
    availabilityLoading.value = true;
    checkoutLoading.value = false;
    try {
      const result = await getMentorAvailability(mentorId, applicationId);
      if (!ownsRequest(requestGeneration, availabilityGeneration, session)) return;
      availability.value = result;
    } catch (caught) {
      if (!ownsRequest(requestGeneration, availabilityGeneration, session)) return;
      availabilityError.value = errorMessage(caught, "Could not load availability.");
    } finally {
      if (ownsRequest(requestGeneration, availabilityGeneration, session)) availabilityLoading.value = false;
    }
  }

  async function startCheckout(payload: Record<string, unknown> | FormData): Promise<string | null> {
    const requestGeneration = ++checkoutGeneration;
    const session = sessionGeneration;
    checkoutLoading.value = true;
    availabilityError.value = "";
    try {
      const result = payload instanceof FormData
        ? await createMentorCheckoutWithMaterials(payload)
        : await createMentorCheckout(payload);
      if (!ownsRequest(requestGeneration, checkoutGeneration, session)) return null;
      return result.checkout_url;
    } catch (caught) {
      if (!ownsRequest(requestGeneration, checkoutGeneration, session)) return null;
      availabilityError.value = errorMessage(caught, "Could not start checkout.");
      checkoutLoading.value = false;
      return null;
    }
  }

  async function loadBooking(bookingId: string): Promise<MentorBookingView | null> {
    const requestGeneration = ++bookingGeneration;
    const session = sessionGeneration;
    if (activeBookingId !== bookingId) {
      activeBookingId = bookingId;
      booking.value = null;
    }
    bookingError.value = "";
    try {
      const result = await getMentorBooking(bookingId) as unknown as MentorBookingView;
      if (!ownsRequest(requestGeneration, bookingGeneration, session)) return null;
      booking.value = result;
      return result;
    } catch (caught) {
      if (!ownsRequest(requestGeneration, bookingGeneration, session)) return null;
      bookingError.value = errorMessage(caught, "Could not load booking.");
      return null;
    }
  }

  async function loadDashboard(): Promise<void> {
    const requestGeneration = ++dashboardGeneration;
    const session = sessionGeneration;
    dashboardLoading.value = dashboard.value === null;
    try {
      const result = await getMentorBookings();
      if (!ownsRequest(requestGeneration, dashboardGeneration, session)) return;
      dashboard.value = result;
      dashboardError.value = "";
    } catch (caught) {
      if (!ownsRequest(requestGeneration, dashboardGeneration, session)) return;
      dashboardError.value = errorMessage(caught, "Could not load mentor sessions.");
    } finally {
      if (ownsRequest(requestGeneration, dashboardGeneration, session)) dashboardLoading.value = false;
    }
  }

  async function reschedule(bookingId: string): Promise<void> {
    if (pendingRescheduleBookingIds.has(bookingId)) return;
    const session = sessionGeneration;
    const observation = rescheduleObservationGeneration;
    pendingRescheduleBookingIds.add(bookingId);
    try {
      await requestMentorReschedule(bookingId);
      if (session !== sessionGeneration || observation !== rescheduleObservationGeneration) return;
      await loadDashboard();
    } catch (caught) {
      if (session !== sessionGeneration || observation !== rescheduleObservationGeneration) return;
      dashboardError.value = errorMessage(caught, "Could not request a new time.");
    } finally {
      if (session === sessionGeneration && observation === rescheduleObservationGeneration) {
        pendingRescheduleBookingIds.delete(bookingId);
      }
    }
  }

  async function requestCancellation(bookingId: string): Promise<void> {
    if (pendingCancellationBookingIds.has(bookingId)) return;
    const session = sessionGeneration;
    const observation = rescheduleObservationGeneration;
    pendingCancellationBookingIds.add(bookingId);
    try {
      await requestMentorCancellation(bookingId);
      if (session !== sessionGeneration || observation !== rescheduleObservationGeneration) return;
      await loadDashboard();
    } catch (caught) {
      if (session !== sessionGeneration || observation !== rescheduleObservationGeneration) return;
      dashboardError.value = errorMessage(caught, "Could not request cancellation.");
    } finally {
      if (session === sessionGeneration && observation === rescheduleObservationGeneration) {
        pendingCancellationBookingIds.delete(bookingId);
      }
    }
  }

  function cancelRescheduleObservation(): void {
    rescheduleObservationGeneration += 1;
    dashboardGeneration += 1;
    pendingRescheduleBookingIds.clear();
    pendingCancellationBookingIds.clear();
  }

  function reset(): void {
    sessionGeneration += 1;
    matchingGeneration += 1;
    availabilityGeneration += 1;
    bookingGeneration += 1;
    dashboardGeneration += 1;
    checkoutGeneration += 1;
    rescheduleObservationGeneration += 1;

    matchingApplication.value = null;
    matchingMentors.value = [];
    matchingLoading.value = false;
    matchingError.value = "";
    availability.value = null;
    availabilityLoading.value = false;
    availabilityError.value = "";
    checkoutLoading.value = false;
    booking.value = null;
    bookingError.value = "";
    activeBookingId = "";
    dashboard.value = null;
    dashboardLoading.value = false;
    dashboardError.value = "";
    pendingRescheduleBookingIds.clear();
    pendingCancellationBookingIds.clear();
  }

  return {
    availability,
    availabilityError,
    availabilityLoading,
    booking,
    bookingError,
    cancelRescheduleObservation,
    checkoutLoading,
    dashboard,
    dashboardError,
    dashboardLoading,
    loadAvailability,
    loadBooking,
    loadDashboard,
    loadMatches,
    matchingApplication,
    matchingError,
    matchingLoading,
    matchingMentors,
    pendingRescheduleBookingIds,
    pendingCancellationBookingIds,
    requestCancellation,
    reset,
    reschedule,
    startCheckout,
  };
});
